import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentNugget, ContentNuggetBookmark, Pagination } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';
import {
  getNuggetCategoryKey,
  handlePanelNuggetClick,
  NUGGET_CATEGORY_LABELS,
  NuggetCategoryKey,
} from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { FilterDropdown } from '@backrs_ui/grow-ui/src/pages/Search/components/Filter/FilterDropdown';
import { Category, SearchCategory } from '@backrs_ui/grow-ui/src/pages/Search/search.types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { useAgentContext } from './BookmarkPageContainer.agentContext';
import { mapBookmarksToContentNugget } from './bookmarks.util';
import { BookmarkHeader } from '../components/BookmarkHeader';

type BookmarkPageProps = Readonly<{
  bookmarks?: ContentNuggetBookmark[];
}>;

const BOOKMARK_STRINGS = appStrings.modules.bookmarks;
const ITEMS_PER_PAGE = 12;

export const BookmarkPage = ({ bookmarks }: BookmarkPageProps) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const bookmarksAsNuggets: ContentNugget[] = mapBookmarksToContentNugget(bookmarks ?? []);

  const [selectedCategories, setSelectedCategories] = useState<SearchCategory[]>([]);

  const filterCategories: Category[] = useMemo(() => {
    const uniqueCategories = new Set<string>();

    bookmarksAsNuggets.forEach((nugget) => {
      const categoryKey = getNuggetCategoryKey(nugget);

      if (categoryKey) {
        uniqueCategories.add(categoryKey);
      }
    });

    return Array.from(uniqueCategories).map((categoryKey) => ({
      key: categoryKey as SearchCategory,
      label: NUGGET_CATEGORY_LABELS[categoryKey as NuggetCategoryKey],
    }));
  }, [bookmarksAsNuggets]);

  // filter bookmarks based on search term and selected categories
  const filteredBookmarks = useMemo(() => {
    const searchFiltered = searchTerm.trim()
      ? bookmarksAsNuggets.filter(
          (nugget) =>
            nugget.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            nugget.description?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : bookmarksAsNuggets;

    const categoryFiltered =
      selectedCategories.length > 0
        ? searchFiltered.filter((nugget) => {
            const category = getNuggetCategoryKey(nugget);

            return category ? selectedCategories.includes(category as SearchCategory) : false;
          })
        : searchFiltered;

    return categoryFiltered;
  }, [bookmarksAsNuggets, searchTerm, selectedCategories]);

  // calculations for pagination
  const totalPages = Math.ceil(filteredBookmarks.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedBookmarks = filteredBookmarks.slice(startIndex, endIndex);
  const isPaginated = totalPages > 1;

  useAgentContext({
    totalBookmarks: bookmarksAsNuggets.length,
    searchTerm,
    selectedCategories,
    availableCategories: filterCategories,
    currentPage,
    totalPages,
    visibleNuggets: paginatedBookmarks,
  });

  const handleOpeningDialog = (nugget: ContentNugget) => {
    handlePanelNuggetClick(nugget, navigate);
  };

  const handlePageChange = (pageNum: number) => {
    setCurrentPage(pageNum);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // reset to page 1 when search term changes
  const handleSearchTermChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1);
  };

  // reset to page 1 when category filter changes
  const handleCategoryFilterChange = (newCategories: SearchCategory[]) => {
    setSelectedCategories(newCategories);
    setCurrentPage(1);
  };

  return (
    <Box sx={containerSx}>
      <BookmarkHeader searchTerm={searchTerm} onSearchTermChange={handleSearchTermChange} />
      {paginatedBookmarks.length > 0 && (
        <Box sx={filterContainerSx}>
          <FilterDropdown
            key={BOOKMARK_STRINGS.filter}
            name={BOOKMARK_STRINGS.filter}
            iconName="FilterLines"
            selectedItems={selectedCategories}
            onSelect={handleCategoryFilterChange}
            hasSelectedItems={selectedCategories.length > 0}
            menuItemGroups={[filterCategories]}
          />
        </Box>
      )}
      {paginatedBookmarks.length === 0 && (
        <Box sx={noBookmarksContainerStyle}>
          <Box component="img" src="/images/no-search-results.svg" />
          <Typography variant="text-lg-regular">{BOOKMARK_STRINGS.noResults}</Typography>
        </Box>
      )}
      <Box sx={contentSx}>
        <Box sx={nuggetListContainerStyle(isPaginated)}>
          {paginatedBookmarks.length > 0 && (
            <NuggetCardList
              nuggets={paginatedBookmarks}
              onClick={handleOpeningDialog}
              direction="row"
            />
          )}
        </Box>
        {isPaginated && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            handlePrevPage={handlePrevPage}
            handleNextPage={handleNextPage}
            sticky
          />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  containerType: 'inline-size',
  width: '100%',
  justifyContent: 'space-between',
  display: 'flex',
  flexDirection: 'column',
};

const contentSx = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
};

const nuggetListContainerStyle =
  (isPaginated: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
    flexGrow: 1,
    maxWidth: '1200px',
    height: '100%',
    mx: 'auto',
    ...(isPaginated && {
      marginBottom: backrs.spacing['spacing-8xl'],
      '@container (min-width: 900px)': {
        marginBottom: backrs.spacing['spacing-9xl'],
      },
    }),
    paddingBottom: backrs.spacing['spacing-lg'],
    '@container (min-width: 900px)': {
      paddingBottom: backrs.spacing['spacing-2xl'],
    },
  });

const filterContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  display: 'flex',
  height: 'fit-content',
  justifyContent: 'flex-end',
  mt: backrs.spacing['spacing-lg'],
  mb: backrs.spacing['spacing-2xl'],
});

const noBookmarksContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  whiteSpace: 'pre-line',
  alignSelf: 'center',
  gap: backrs.spacing['spacing-5xl'],
  mt: backrs.spacing['spacing-4xl'],
  '@container (min-width: 600px)': {
    mt: backrs.spacing['spacing-8xl'],
  },
  img: {
    width: '100%',
  },
});
